import React, { useContext, useState, useMemo } from 'react';

import AuthManager, { IAuthManager } from 'utils/authManager';
import * as errors from 'utils/errors';

import * as Interfaces from './interfaces';

/**
 * React Context API used across the login API
 */
interface LoginContextProps extends Interfaces.LoginConsumerContextProps {
  /**
   * change the login state of the
   */
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * authentication manager object
   */
  authManager: IAuthManager;
  /**
   * Does the user logged in or out from the system. Defaults to 'false'
   */
  isLoggedIn?: boolean;
}
type LoginProviderProps = React.FC<
  Omit<LoginContextProps, 'isLoggedIn' | 'setIsLoggedIn' | 'authManager'>
>;

const LoginContext = React.createContext({} as LoginContextProps);

/**
 * Get the SideNav context provider with the different state data used across the entire sideNav
 */
export const LoginProvider: LoginProviderProps = ({
  client_id,
  loginUrl,
  linkTarget,
  redirect_uri,
  authStore = sessionStorage,
  children
}) => {
  if (!client_id) {
    throw new errors.SVError("LoginContext error: missing param 'client_id'");
  }
  if (!loginUrl) {
    throw new errors.SVError("LoginContext error: missing param 'loginUrl'");
  }
  if (!redirect_uri) {
    throw new errors.SVError("LoginContext error: missing param 'redirect_uri'");
  }
  const authManager = useMemo(() => {
    return new AuthManager(loginUrl, client_id, redirect_uri, authStore);
  }, [loginUrl, client_id, redirect_uri, authStore]);
  const userData = authManager.getUserInfo();
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!userData);

  // Use useMemo for optimization reasons. This says don't give back a new state unless isLoggedIn, loginUrl, loggedInItems changes (similar to components/Wizard/context/context.tsx)
  const value = useMemo(
    () => ({
      setIsLoggedIn,
      loginUrl,
      client_id,
      linkTarget,
      authManager,
      redirect_uri,
      authStore,
      isLoggedIn
    }),
    [
      isLoggedIn,
      authStore,
      authManager,
      linkTarget,
      client_id,
      redirect_uri,
      setIsLoggedIn,
      loginUrl
    ]
  );
  return <LoginContext.Provider value={value}>{children}</LoginContext.Provider>;
};

/**
 * Get access to the login main context data from the transaction
 */
export function useLoginConsumerContext(): Interfaces.LoginConsumerContextProps {
  const context = useContext(LoginContext);
  if (!context) {
    throw new errors.SVError('LoginContext must be used with LoginProvider!');
  }
  return context;
}

/**
 * Get access to the login main context data (used only inside the library layer)
 */
export function useLoginContext(): LoginContextProps {
  const context = useContext(LoginContext);
  if (!context) {
    throw new errors.SVError('LoginContext must be used with LoginProvider!');
  }
  return context;
}
